// User Access Service
// Fetches and caches user access levels from Cognito database

import { apiClient } from "./api";

class UserAccessService {
	constructor() {
		this.cache = new Map();
		this.cacheTimeout = 30 * 60 * 1000; // 30 minutes — aligns with Cognito token lifetime
		this.pendingRequests = new Map(); // Deduplicate in-flight requests
	}

	/**
	 * Get user access level from API
	 * @param {string} email - User's email address
	 * @returns {Promise<Object>} User access data
	 */
	async getUserAccess(email) {
		if (!email) {
			console.warn("⚠️ No email provided to getUserAccess");
			return {
				success: false,
				can_write: false,
				user_access: "read",
				user_role: "user",
				is_admin: false,
			};
		}

		// Check cache first
		const cached = this.cache.get(email);
		if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
			console.log("✅ Using cached access data for:", email);
			return cached.data;
		} // If there's already a pending request for this email, reuse it
		if (this.pendingRequests.has(email)) {
			console.log("⏳ Reusing in-flight request for:", email);
			return this.pendingRequests.get(email);
		}

		const fetchPromise = this._fetchUserAccess(email);
		this.pendingRequests.set(email, fetchPromise);
		fetchPromise.finally(() => this.pendingRequests.delete(email));
		return fetchPromise;
	}

	async _fetchUserAccess(email) {
		try {
			console.log("🔍 Fetching access data for:", email);
			const response = await apiClient.get("/cognito/user-access", {
				params: { email },
			});

			if (response.data.success) {
				// Cache the result
				this.cache.set(email, {
					data: response.data,
					timestamp: Date.now(),
				});

				console.log("✅ User access fetched:", {
					email,
					can_write: response.data.can_write,
					user_access: response.data.user_access,
					user_role: response.data.user_role,
					is_admin: response.data.is_admin,
				});

				return response.data;
			} else {
				console.warn("⚠️ Failed to fetch user access, defaulting to read-only");
				return {
					success: false,
					can_write: false,
					user_access: "read",
					user_role: "user",
					is_admin: false,
				};
			}
		} catch (error) {
			console.error("❌ Error fetching user access:", error);
			return {
				success: false,
				can_write: false,
				user_access: "read",
				user_role: "user",
				is_admin: false,
				error: error.message,
			};
		}
	}

	/**
	 * Get cached user access synchronously (for authorization checks)
	 * Returns null if not cached - caller should handle appropriately
	 * @param {string} email - User's email address
	 * @returns {Object|null} Cached user access data or null
	 */
	getCachedUserAccess(email) {
		if (!email) {
			return null;
		}

		const cached = this.cache.get(email);
		if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
			// Return normalized format with canWrite property
			return {
				canWrite: cached.data.can_write === true,
				canEditProtected: cached.data.can_write === true,
				isAdmin: cached.data.is_admin === true,
				userAccess: cached.data.user_access,
				userRole: cached.data.user_role,
			};
		}

		return null;
	}

	/**
	 * Check if user has write access
	 * @param {string} email - User's email address
	 * @returns {Promise<boolean>} True if user can write
	 */
	async canUserWrite(email) {
		const access = await this.getUserAccess(email);
		return access.can_write === true;
	}

	/**
	 * Check if user is admin
	 * @param {string} email - User's email address
	 * @returns {Promise<boolean>} True if user is admin
	 */
	async isUserAdmin(email) {
		const access = await this.getUserAccess(email);
		return access.is_admin === true;
	}

	/**
	 * Clear cache for a specific user or all users
	 * @param {string} email - Optional email to clear specific user cache
	 */
	clearCache(email = null) {
		if (email) {
			this.cache.delete(email);
			console.log("🗑️ Cleared cache for:", email);
		} else {
			this.cache.clear();
			console.log("🗑️ Cleared all access cache");
		}
	}

	/**
	 * Preload user access (useful on app startup)
	 * @param {string} email - User's email address
	 */
	async preloadUserAccess(email) {
		if (email) {
			await this.getUserAccess(email);
		}
	}
}

// Create singleton instance
const userAccessService = new UserAccessService();

export default userAccessService;
export { UserAccessService };
